import { useState, type FormEvent } from "react";
import { submitInquiry, type InquiryPayload, type InquiryType } from "@/lib/api";
import styles from "./InquiryForm.module.css";

type FieldName = keyof InquiryPayload;

interface InquiryFormProps {
  type: InquiryType;
  /** Extra fields to render beyond the standard name/email/phone/message. */
  extras?: "stay" | "event" | "subject" | "home";
  submitLabel?: string;
  successTitle?: string;
  successBody?: string;
}

const INQUIRY_SUBJECTS = [
  "Events at Villa Rosa",
  "Overnight stay at Villa Rosa",
  "Questions about the area",
  "Feedback for our team",
  "Something else",
];

const EVENT_TYPES = [
  "Wedding",
  "Corporate retreat",
  "Family reunion",
  "Birthday / celebration",
  "Photo / video shoot",
  "Overnight stay only",
  "Other",
];

const OVERNIGHT_OPTIONS = ["Yes", "No", "Not sure yet"];

const BUDGET_OPTIONS = [
  "Under $5,000",
  "$5,000 – $10,000",
  "$10,000 – $20,000",
  "$20,000+",
  "Not sure yet",
];

export function InquiryForm({
  type,
  extras,
  submitLabel = "Send",
  successTitle = "Thank you!",
  successBody = "Your message has been sent. We'll be in touch shortly.",
}: InquiryFormProps) {
  const [status, setStatus] = useState<"idle" | "sending" | "done" | "error">("idle");
  const [error, setError] = useState<string>("");

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (status === "sending") return;
    setStatus("sending");
    setError("");

    const form = e.currentTarget;
    const fd = new FormData(form);
    const value = (name: FieldName) => (fd.get(name) as string | null)?.trim() || undefined;

    const payload: InquiryPayload = {
      type,
      firstName: value("firstName") ?? "",
      lastName: value("lastName") ?? "",
      email: value("email") ?? "",
      phone: value("phone"),
      message: value("message") ?? "",
      subject: value("subject"),
      checkIn: value("checkIn"),
      checkOut: value("checkOut"),
      guests: value("guests"),
      eventType: value("eventType"),
      eventDate: value("eventDate"),
      overnightStay: value("overnightStay"),
      budget: value("budget"),
      // VR-3: record the page so the CRM can distinguish Home from Book an Event
      // inquiries even though both use type="event".
      page: typeof window !== "undefined" ? window.location.pathname : undefined,
    };

    const result = await submitInquiry(payload);
    if (result.ok) {
      setStatus("done");
      form.reset();
    } else {
      setStatus("error");
      setError(result.error ?? "Something went wrong.");
    }
  }

  if (status === "done") {
    return (
      <div className={styles.success} role="status">
        <span className={styles.successMark} aria-hidden="true">
          ✓
        </span>
        <h3>{successTitle}</h3>
        <p>{successBody}</p>
      </div>
    );
  }

  return (
    <form className={styles.form} onSubmit={onSubmit} noValidate>


      <div className={styles.row}>
        <Field label="First name" name="firstName" required autoComplete="given-name" />
        <Field label="Last name" name="lastName" required autoComplete="family-name" />
      </div>
      <div className={styles.row}>
        <Field label="Email" name="email" type="email" required autoComplete="email" />
        <Field label="Phone" name="phone" type="tel" autoComplete="tel" />
      </div>

      {extras === "subject" && (
        <Select label="What is your inquiry regarding?" name="subject" options={INQUIRY_SUBJECTS} />
      )}

      {extras === "stay" && (
        <div className={styles.row3}>
          <Field label="Check-in" name="checkIn" type="date" />
          <Field label="Check-out" name="checkOut" type="date" />
          <Field label="Guests" name="guests" type="number" placeholder="2" />
        </div>
      )}

      {extras === "event" && (
        <div className={styles.row}>
          <Select label="Event type" name="eventType" options={EVENT_TYPES} />
          <Field label="Preferred date" name="eventDate" type="date" />
        </div>
      )}

      {extras === "home" && (
        <>
          <div className={styles.row}>
            <Select label="Event type" name="eventType" options={EVENT_TYPES} />
            <Field label="Preferred date" name="eventDate" type="date" />
          </div>
          <div className={styles.row3}>
            <Field label="Guest count" name="guests" type="number" placeholder="50" />
            <Select label="Overnight stay?" name="overnightStay" options={OVERNIGHT_OPTIONS} />
            <Select label="Estimated budget" name="budget" options={BUDGET_OPTIONS} />
          </div>
        </>
      )}

      <label className={styles.field}>
        <span className={styles.label}>
          How can we help?{" "}
          <span className={styles.req} aria-hidden="true">
            *
          </span>
        </span>
        <textarea name="message" rows={5} required className={styles.textarea} />
      </label>

      {status === "error" && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}

      <button type="submit" className="btn btn--primary" disabled={status === "sending"}>
        {status === "sending" ? "Sending…" : submitLabel}
      </button>
    </form>
  );
}

function Field({
  label,
  name,
  type = "text",
  required = false,
  autoComplete,
  placeholder,
}: {
  label: string;
  name: FieldName;
  type?: string;
  required?: boolean;
  autoComplete?: string;
  placeholder?: string;
}) {
  return (
    <label className={styles.field}>
      <span className={styles.label}>
        {label}
        {required && (
          <span className={styles.req} aria-hidden="true">
            {" "}
            *
          </span>
        )}
      </span>
      <input
        className={styles.input}
        name={name}
        type={type}
        required={required}
        autoComplete={autoComplete}
        placeholder={placeholder}
      />
    </label>
  );
}

function Select({
  label,
  name,
  options,
}: {
  label: string;
  name: FieldName;
  options: string[];
}) {
  return (
    <label className={styles.field}>
      <span className={styles.label}>{label}</span>
      <select className={styles.input} name={name} defaultValue="">
        <option value="" disabled>
          Select…
        </option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </label>
  );
}
